import React from "react";
import { MapPin, Phone, Instagram, Facebook, Train } from "lucide-react";
import { BAR, MAPS_CID_URL, MAPS_EMBED_URL } from "@/lib/info";

// Plain query-based embed — no API key required, signs itself
const MAPS_EMBED = MAPS_EMBED_URL;

const HoursTable = () => (
  <div
    data-testid="footer-hours"
    className="bg-[#1A1A1A] rounded-lg p-6 border border-[#333] shadow-lg"
  >
    <h3 className="font-display text-2xl tracking-wide text-white mb-4 border-b border-[#262626] pb-3">
      Operational Hours
    </h3>
    <ul className="space-y-3 font-mono-plex text-sm">
      {BAR.hours.map((h) => {
        const isHighlight = h.id === "tue-fri";
        return (
          <li
            key={h.id}
            className={`flex justify-between gap-6 ${
              isHighlight
                ? "text-[#FFB800] font-semibold neon-amber"
                : "text-[#A3A3A3]"
            }`}
          >
            <span>{h.days}:</span>
            <span
              className={`tabular-nums ${
                isHighlight ? "" : "text-[#F5F2E6]"
              }`}
            >
              {h.time}
              {isHighlight ? " *" : ""}
            </span>
          </li>
        );
      })}
    </ul>
    <p className="text-xs italic text-[#777] mt-4">
      * Happy Hour: 2:00 PM – 7:00 PM
    </p>
  </div>
);

const ActionButtons = () => (
  <div className="flex flex-col sm:flex-row gap-3">
    <a
      href={MAPS_CID_URL}
      target="_blank"
      rel="noreferrer"
      data-testid="footer-cta-open-maps"
      className="btn-press inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-md text-black bg-[#FFB800] hover:bg-[#ffc43d] font-mono-plex text-xs uppercase tracking-[0.2em] font-bold transition-colors"
      style={{ boxShadow: "0 0 20px rgba(255, 184, 0, 0.25)" }}
    >
      <MapPin size={16} strokeWidth={2.5} />
      Open in Google Maps
    </a>
    <a
      href={`tel:${BAR.phone.tel}`}
      data-testid="footer-cta-call"
      className="btn-press inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-md border border-[#FF2A2A] text-[#FF2A2A] bg-transparent hover:bg-[#FF2A2A] hover:text-white font-mono-plex text-xs uppercase tracking-[0.2em] font-bold transition-colors"
      style={{ boxShadow: "0 0 15px rgba(255, 42, 42, 0.12)" }}
    >
      <Phone size={16} strokeWidth={2.5} />
      Call the Bar
    </a>
  </div>
);

const SocialRow = () => (
  <div className="flex items-center gap-2 pt-2">
    <a
      href={BAR.social.instagram}
      target="_blank"
      rel="noreferrer"
      data-testid="footer-social-instagram"
      aria-label="Sunny's on Instagram"
      className="btn-press w-10 h-10 inline-flex items-center justify-center border border-[#262626] hover:border-[#FFB800] hover:text-[#FFB800] text-[#A3A3A3] rounded-md"
    >
      <Instagram size={16} strokeWidth={2.5} />
    </a>
    <a
      href={BAR.social.facebook}
      target="_blank"
      rel="noreferrer"
      data-testid="footer-social-facebook"
      aria-label="Sunny's on Facebook"
      className="btn-press w-10 h-10 inline-flex items-center justify-center border border-[#262626] hover:border-[#FFB800] hover:text-[#FFB800] text-[#A3A3A3] rounded-md"
    >
      <Facebook size={16} strokeWidth={2.5} />
    </a>
    <span className="ml-2 font-mono-plex text-[10px] tracking-[0.22em] uppercase text-[#A3A3A3]">
      Follow the neighborhood
    </span>
  </div>
);

const InfoColumn = () => (
  <div className="space-y-6">
    <span className="text-[#FF2A2A] text-xs font-bold tracking-[0.3em] uppercase font-mono-plex">
      Visit Us
    </span>
    <h2
      data-testid="location-heading"
      className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-white tracking-tight uppercase"
    >
      In the Heart of
      <br />
      <span className="text-stroke">the Grid.</span>
    </h2>

    <div className="flex items-start gap-3">
      <MapPin
        size={22}
        strokeWidth={2.5}
        className="text-[#FFB800] mt-1 shrink-0"
      />
      <p
        data-testid="footer-address"
        className="font-display text-2xl md:text-3xl text-[#FFB800] neon-amber leading-tight"
      >
        {BAR.address.full}
      </p>
    </div>

    <p
      data-testid="metrorail-paragraph"
      className="text-sm text-[#A3A3A3] leading-relaxed"
    >
      Perfectly situated downtown near the{" "}
      <span className="text-white font-semibold">
        METRORail line intersections
      </span>
      . Drop in to bypass stadium inflation prices before heading off to catch
      the big game or concert.
    </p>

    <div className="flex items-center gap-2 text-[#A3A3A3] font-mono-plex text-[10px] tracking-[0.25em] uppercase">
      <Train size={12} strokeWidth={2.5} className="text-[#FFB800]" />
      Steps from METRORail Central Station
    </div>

    <HoursTable />
    <ActionButtons />
    <SocialRow />
  </div>
);

const MapColumn = () => (
  <div
    data-testid="footer-map-wrapper"
    className="w-full h-[420px] md:h-[560px] rounded-xl overflow-hidden border-2 border-[#333] bg-[#1A1A1A] relative"
    style={{ boxShadow: "0 0 30px rgba(0, 0, 0, 0.5)" }}
  >
    <iframe
      title="Sunny's Bar — 902 Capitol St, Houston TX 77002"
      data-testid="footer-map-iframe"
      src={MAPS_EMBED}
      width="100%"
      height="100%"
      style={{
        border: 0,
        filter: "grayscale(0.8) contrast(1.2) invert(0.9) hue-rotate(180deg)",
      }}
      allowFullScreen=""
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
    />
    <div className="absolute top-4 left-4 bg-black/85 backdrop-blur border border-[#FFB800]/40 px-4 py-2 font-mono-plex text-[10px] tracking-[0.25em] uppercase text-[#FFB800] rounded">
      ↳ Capitol &amp; Main · Downtown Houston
    </div>
  </div>
);

const BigSignature = () => (
  <div className="relative overflow-hidden border-t border-[#262626] mt-16">
    <div className="px-6 md:px-12 lg:px-24 py-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
      <h3
        data-testid="footer-bigmark"
        className="font-display text-[20vw] md:text-[14vw] leading-[0.8] text-stroke select-none"
      >
        SUNNY<span className="text-[#FF2A2A]">'</span>S
      </h3>
      <div className="font-mono-plex text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] text-right">
        © {new Date().getFullYear()} · {BAR.name} · Drink water · Tip your
        bartender
      </div>
    </div>
  </div>
);

const FooterLocation = () => {
  return (
    <footer
      id="location"
      data-testid="footer-location"
      className="relative bg-[#121212] text-white border-t border-[#262626]"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-20 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-start">
        <InfoColumn />
        <MapColumn />
      </div>
      <BigSignature />
    </footer>
  );
};

export default FooterLocation;
